import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AuthForm } from "@/components/auth/AuthForm";
import { DecisionProcessVisual } from "@/components/auth/DecisionProcessVisual";
import { DecisionProcessMobile } from "@/components/auth/DecisionProcessMobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import quentariLogo from "@/assets/quentari-logo.png";
import { ArrowLeft, Loader2, Shield, Lock, Server } from "lucide-react";

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Trigger animations after mount
    const timer = setTimeout(() => setIsLoaded(true), 50);
    
    // Check if already logged in
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        navigate("/");
      }
    });
    
    return () => clearTimeout(timer);
  }, [navigate]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!resetEmail) {
      toast({
        title: "Fehler",
        description: "Bitte gib deine E-Mail-Adresse ein.",
        variant: "destructive",
      });
      return;
    }

    setIsResetting(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth`,
      });

      if (error) throw error;

      toast({
        title: "E-Mail versendet",
        description: "Überprüfe dein Postfach für den Zurücksetzungslink.",
      });

      setShowPasswordReset(false);
      setResetEmail("");
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "Ein Fehler ist aufgetreten.",
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
    }
  };

  // Shared background component
  const AuthBackground = () => (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base gradient for warmth */}
      <div 
        className="absolute inset-0 transition-opacity duration-1000"
        style={{
          opacity: isLoaded ? 1 : 0,
          background: `
            radial-gradient(ellipse 80% 50% at 50% 0%, hsl(var(--primary) / 0.04) 0%, transparent 60%),
            radial-gradient(ellipse 60% 40% at 0% 50%, hsl(var(--primary) / 0.03) 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 100% 50%, hsl(var(--primary) / 0.03) 0%, transparent 50%)
          `,
        }}
      />
      
      {/* Large grid - primary structure */}
      <div 
        className="absolute inset-[-20%] transition-opacity duration-1500"
        style={{
          opacity: isLoaded ? 1 : 0,
          transitionDelay: '200ms',
          backgroundImage: `
            linear-gradient(hsl(var(--primary) / 0.05) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--primary) / 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 40%, black 10%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 40%, black 10%, transparent 70%)',
        }}
      />
      
      {/* Small grid - detail layer */}
      <div 
        className="absolute inset-[-10%] transition-opacity duration-1500"
        style={{
          opacity: isLoaded ? 1 : 0,
          transitionDelay: '400ms',
          backgroundImage: `
            linear-gradient(hsl(var(--primary) / 0.02) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--primary) / 0.02) 1px, transparent 1px)
          `,
          backgroundSize: '15px 15px',
          maskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, black 5%, transparent 60%)',
          WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, black 5%, transparent 60%)',
        }}
      />
      
      {/* Intersection glow points */}
      <div 
        className="absolute inset-[-15%] transition-opacity duration-1500"
        style={{
          opacity: isLoaded ? 1 : 0,
          transitionDelay: '600ms',
          backgroundImage: `radial-gradient(circle at center, hsl(var(--primary) / 0.06) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse 50% 50% at 50% 45%, black 0%, transparent 60%)',
          WebkitMaskImage: 'radial-gradient(ellipse 50% 50% at 50% 45%, black 0%, transparent 60%)',
        }}
      />
      
      {/* Decorative floating lines */}
      <div 
        className="absolute top-1/4 left-8 w-px h-24 bg-gradient-to-b from-transparent via-primary/15 to-transparent hidden md:block animate-[float_8s_ease-in-out_infinite]"
      />
      <div 
        className="absolute top-1/3 right-8 w-px h-20 bg-gradient-to-b from-transparent via-primary/10 to-transparent hidden md:block animate-[float_10s_ease-in-out_infinite]"
        style={{ animationDelay: '2s' }}
      />
      <div 
        className="absolute bottom-1/4 left-16 w-20 h-px bg-gradient-to-r from-transparent via-primary/10 to-transparent hidden md:block animate-[float-horizontal_9s_ease-in-out_infinite]"
        style={{ animationDelay: '1s' }}
      />
      
      {/* Vignette for depth */}
      <div 
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 100% 100% at 50% 50%, transparent 20%, hsl(var(--background)) 100%)`,
        }}
      />
    </div>
  );

  if (showPasswordReset) {
    return (
      <div className="min-h-screen bg-background flex flex-col relative">
        <AuthBackground />
        
        <header className="w-full border-b border-border/50 backdrop-blur-sm bg-background/80 relative z-10">
          <div className="container flex h-16 items-center justify-between max-w-7xl mx-auto px-4 md:px-6">
            <Link to="/" className="transition-opacity hover:opacity-80">
              <img src={quentariLogo} alt="Quentari" className="h-8 w-auto" />
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Zur Startseite
              </Button>
            </Link>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-4 md:px-6 pb-16 relative z-10">
          <div 
            className={`w-full max-w-md transition-all duration-700 ease-out ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <Button
              variant="ghost"
              onClick={() => setShowPasswordReset(false)}
              className="mb-6 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Zurück zur Anmeldung
            </Button>

            <div className="mb-8">
              <h1 
                className="text-2xl md:text-3xl font-semibold font-serif text-foreground mb-3"
                style={{ 
                  textShadow: `
                    0 1px 2px hsl(var(--foreground) / 0.04),
                    0 4px 8px hsl(var(--foreground) / 0.02)
                  `,
                }}
              >
                Passwort zurücksetzen
              </h1>
              <p className="text-muted-foreground">
                Gib deine E-Mail-Adresse ein, um einen Link zum Zurücksetzen zu erhalten.
              </p>
            </div>

            <div className="relative">
              {/* Card glow effect */}
              <div 
                className="absolute -inset-px rounded-2xl bg-gradient-to-b from-primary/10 via-transparent to-transparent opacity-60"
              />
              <div className="relative bg-card/80 backdrop-blur-sm border border-border/60 rounded-2xl p-8 shadow-xl shadow-primary/5">
                <form onSubmit={handlePasswordReset} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email" className="text-sm font-medium">E-Mail</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="name@beispiel.de"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      disabled={isResetting}
                      className="h-12 bg-background/50 border-border/60 focus:border-primary/50 focus:ring-primary/20 transition-all"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 text-base font-medium shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300"
                    disabled={isResetting}
                  >
                    {isResetting && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                    {isResetting ? "Wird versendet..." : "Link senden"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col relative">
      <AuthBackground />
      
      <header className="w-full border-b border-border/50 backdrop-blur-sm bg-background/80 relative z-10">
        <div className="container flex h-16 items-center justify-between max-w-7xl mx-auto px-4 md:px-6">
          <Link to="/" className="transition-opacity hover:opacity-80">
            <img src={quentariLogo} alt="Quentari" className="h-8 w-auto" />
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Zur Startseite
            </Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 md:px-6 py-8 md:py-16 relative z-10">
        <div className="w-full max-w-5xl flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          
          {/* Left side: Visual (hidden on mobile) */}
          <div 
            className={`hidden lg:flex flex-1 items-center justify-center transition-all duration-1000 ${
              isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <DecisionProcessVisual />
          </div>

          {/* Right side: Auth form */}
          <div 
            className={`w-full max-w-sm transition-all duration-700 ease-out ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {/* Mobile process visual */}
            <div className="lg:hidden mb-6">
              <DecisionProcessMobile />
            </div>

            {/* Headline section with visual depth */}
            <div className="mb-8 text-center lg:text-left">
              <h1 
                className="text-2xl sm:text-3xl md:text-4xl font-semibold font-serif text-foreground mb-2 relative"
                style={{ 
                  textShadow: `
                    0 1px 2px hsl(var(--foreground) / 0.04),
                    0 4px 8px hsl(var(--foreground) / 0.02),
                    0 8px 16px hsl(var(--primary) / 0.04)
                  `,
                }}
              >
                Klarheit beginnt hier.
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed">
                60 Minuten. Eine Entscheidung. Dokumentiert.
              </p>
            </div>

            {/* Auth card with glass effect */}
            <div className="relative">
              {/* Card glow effect */}
              <div 
                className="absolute -inset-px rounded-2xl bg-gradient-to-b from-primary/10 via-transparent to-transparent opacity-60"
              />
              <div className="relative bg-card/80 backdrop-blur-sm border border-border/60 rounded-2xl p-6 shadow-xl shadow-primary/5">
                <Tabs defaultValue="login" className="w-full">
                  <TabsList className="relative grid grid-cols-2 h-12 w-full rounded-xl bg-muted/50 p-1.5 mb-6 border border-border/40">
                    <TabsTrigger 
                      value="login"
                      className="relative z-10 rounded-lg text-sm font-medium transition-all duration-200 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-md data-[state=active]:shadow-primary/10 data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground/70"
                    >
                      Anmelden
                    </TabsTrigger>
                    <TabsTrigger 
                      value="signup"
                      className="relative z-10 rounded-lg text-sm font-medium transition-all duration-200 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-md data-[state=active]:shadow-primary/10 data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground/70"
                    >
                      Registrieren
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="login" className="mt-0">
                    <AuthForm mode="login" />
                    <div className="mt-4 text-center">
                      <button
                        type="button"
                        onClick={() => setShowPasswordReset(true)}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
                      >
                        Passwort vergessen?
                      </button>
                    </div>
                  </TabsContent>

                  <TabsContent value="signup" className="mt-0">
                    <AuthForm mode="signup" />
                  </TabsContent>
                </Tabs>
              </div>
            </div>

            {/* Trust indicators */}
            <div 
              className={`mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-xs text-muted-foreground/70 transition-all duration-700 ${
                isLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ transitionDelay: '400ms' }}
            >
              <span className="flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5" />
                Datenschutz in Arbeit
              </span>
              <span className="flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5" />
                Verschlüsselte Daten
              </span>
              <span className="flex items-center gap-1.5">
                <Server className="h-3.5 w-3.5" />
                EU-Hosting geplant
              </span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Auth;
